"use client";
import { ArrowDown } from "@/components/icons/arrow";
import { Image } from "@/components/shared/image";
import Lightbox from "@/components/shared/ligthbox";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useLocalRouter from "@/hooks/use-router";
import { useWindowSize } from "@/hooks/use-window-size";
import { useTranslation } from "@/i18n/client";
import { Lang } from "@/i18n/settings";
import { cn } from "@/lib/utils";
import {
  getHonorees,
  HonoreesCategoriesProps,
  HonoreesProps,
} from "@/services/honorees/honorees.server";
import { MetaProps } from "@/types";
import { Search } from "lucide-react";
import React, { useEffect, useId, useState } from "react";
import { Categories } from "./categories";

const MAX_HEIGHT = 600;
const MIN_HEIGHT = 500;

type Props = {
  data: HonoreesProps[];
  meta?: MetaProps["pagination"];
  lang: Lang;
  searchParams: { year: string; category: string };
  categories: HonoreesCategoriesProps[];
  tabs: string[];
};

type HonoreesData = HonoreesProps & {
  height?: number;
  fill?: boolean;
  absolute?: boolean;
};

export function Cards({
  data: initialData,
  meta,
  lang,
  tabs,
  searchParams,
  categories: catRes,
}: Props) {
  const { t } = useTranslation(lang, "honorees");

  const [categories] = useState(catRes);

  const { width } = useWindowSize();

  const [index, setIndex] = useState(-1);
  const [data, setData] = useState<HonoreesData[][]>(convertArray(initialData));
  const [tempData, setTempData] = useState<HonoreesData[]>(mapToHonoreesProps(initialData));
  const router = useLocalRouter();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState((meta?.pageCount ?? 1) > 1);

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  async function loadMore() {
    if (!hasMore || loading) return;

    setLoading(true);

    const res = await getHonorees({
      locale: lang,
      page: page + 1,
      year: searchParams?.year
        ? searchParams?.year === "All"
          ? undefined
          : Number(searchParams.year)
        : undefined,
      category: searchParams?.category,
    });

    if (res?.data) {
      const td = mapToHonoreesProps([...tempData, ...res.data]);
      setTempData(td);

      const d = convertArray([...data.flat(), ...res.data]);

      setData(d);
      setPage(page + 1);
      setHasMore(res.meta.pageCount > page + 1);
    }

    setLoading(false);
  }

  function convertArray(arr: HonoreesData[]): HonoreesData[][] {
    const result = [] as HonoreesData[][];
    const itemsPerChildArray = 4;

    for (let i = 0; i < arr.length; i += itemsPerChildArray) {
      const childArray = arr.slice(i, i + itemsPerChildArray);

      for (let j = 0; j < childArray.length; j++) {
        // Determine if the parent array index (i) is odd or even
        const isParentOdd = Math.floor(i / itemsPerChildArray) % 2 === 1;
        // Determine the height based on the child array index (j)
        if (isParentOdd) {
          // Parent index is odd
          childArray[j].height = j % 2 === 0 ? MIN_HEIGHT : MAX_HEIGHT;
          childArray[j].fill = j % 2 === 0;
          childArray[j].absolute = j % 2 === 1;
        } else {
          // Parent index is even
          childArray[j].height = j % 2 === 0 ? MAX_HEIGHT : MIN_HEIGHT;
        }
      }

      result.push(childArray);
    }

    return result;
  }

  function mapToHonoreesProps(items: HonoreesProps[]): HonoreesData[] {
    return items.map((item, index) => {
      const newItem = {
        ...item,
        height: index % 2 === 0 ? MAX_HEIGHT : MIN_HEIGHT,
      } as HonoreesProps & {
        height: number;
        fill?: boolean;
        absolute?: boolean;
      };

      if (index % 3 === 1) {
        if (newItem.height === MAX_HEIGHT) {
          newItem.absolute = true;
        } else {
          newItem.fill = true;
        }
      }

      return newItem as HonoreesProps & {
        height: number;
        fill?: boolean;
        absolute?: boolean;
      };
    });
  }

  useEffect(() => {
    if (page > 1) return;

    setTempData(mapToHonoreesProps(initialData));
    const d = convertArray(initialData);

    setData(d);
    setHasMore((meta?.pageCount ?? 1) > 1);
  }, [initialData, page]);

  return (
    <>
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
        <h1 className="text-3xl text-secondary md:flex-[0.3] xl:flex-[0.6] xl:text-4xl ltr:font-playfair">
          {t("pageTitle")}
        </h1>
        <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Categories
            onClick={() => {
              setPage(1);
            }}
            placeholder={t("allYears")}
            searchParams={searchParams}
            tabs={tabs}
          />
          <div className="flex max-w-[350px]">
            <Select
              onValueChange={(e) => {
                setPage(1);

                router.push({
                  search: {
                    category: e.startsWith("All") ? "" : e,
                  },
                });
              }}
              dir={lang === "en" ? "ltr" : "rtl"}
            >
              <SelectTrigger className="h-12 min-w-[130px] !rounded-[1000px] bg-foreground px-4 text-secondary backdrop-blur-md transition-all duration-150 ease-in">
                <SelectValue placeholder={t("allCategories")} />
              </SelectTrigger>
              <SelectContent
                avoidCollisions={false}
                align="end"
                className="overflow-y-auto bg-background text-secondary landscape:max-[800px]:max-h-[200px]"
              >
                <SelectItem value={"All"}>{t("allCategories")}</SelectItem>
                {categories?.map((category) => (
                  <SelectItem key={category?.title} value={category?.title}>
                    {category.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {isClient && (
        <>
          {tempData.length === 0 ? (
            <div className="flex h-full min-h-[500px] flex-1 items-center justify-center">
              {t("noDataFound")}.
            </div>
          ) : (
            <div className="flex h-full flex-col gap-12 sm:gap-20">
              {width > 1270 ? (
                <DesktopCards setIndex={setIndex} data={data} />
              ) : (
                <MobileCards setIndex={setIndex} data={tempData} />
              )}

              <div className={cn("flex items-center justify-center", !hasMore && "hidden")}>
                <Button
                  onClick={loadMore}
                  disabled={loading}
                  variant="outline"
                  className="relative z-20 flex h-12 max-w-fit gap-2 rounded-full border-0 bg-foreground px-8 font-normal uppercase text-primary backdrop-blur-md transition-all duration-150 ease-in hover:border-secondary hover:text-secondary/90 lg:h-14 xl:text-base ltr:font-sans"
                >
                  <svg
                    className="pointer-events-none absolute inset-0 -z-10 h-full w-full"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                  >
                    <defs>
                      <linearGradient
                        id={"load-more"}
                        x1="126"
                        y1="0"
                        x2="126"
                        y2="64"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#F0DFC1" />
                        <stop offset="0.791452" stopColor="#C89452" />
                        <stop offset="1" stopColor="#B07F44" />
                      </linearGradient>
                    </defs>

                    <rect
                      x="0.5"
                      y="0.5"
                      width="calc(100% - 1px)"
                      height="calc(100% - 1px)"
                      rx="31.5"
                      ry="31.5"
                      stroke={"url(#load-more)"}
                      strokeWidth="1"
                    />
                  </svg>
                  {t("loadMore")}
                  <ArrowDown />
                </Button>
              </div>

              <Lightbox
                images={data.flat().map((item) => ({
                  src: item.image_link,
                  alt: item.name,
                  title: item?.name,
                  description: item?.description,
                }))}
                index={index}
                onClose={() => {
                  setIndex(-1);
                }}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}

function DesktopCards({
  data,
  setIndex,
}: {
  data: HonoreesData[][];
  setIndex: (index: number) => void;
}) {
  const cardGradientId = useId();

  return (
    <div className="relative flex h-full flex-wrap gap-6 overflow-hidden">
      {data.map((item, itdx) => (
        <React.Fragment key={itdx}>
          {item.map((itm, idx) => (
            <div
              key={itm.id}
              className={
                "relative min-w-full flex-1 sm:min-w-[40%] sm:max-w-[49%] lg:min-w-[20%] lg:max-w-[25%] xl:max-w-[23.9%]"
              }
            >
              <div
                className={cn(
                  "flex flex-col gap-4 rounded-[max(2.083vw,30px)] bg-[#3A285C38] p-4 pb-8 backdrop-blur-md sm:h-[500px]",
                  itm.absolute ? "bottom-0 left-0 right-0 lg:absolute" : "",
                  !itm.description && "pb-4"
                )}
                style={{ height: `${itm.height}px` }}
              >
                <svg
                  className="pointer-events-none absolute inset-0 z-10 h-full w-full"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                >
                  <defs>
                    <linearGradient
                      id={cardGradientId}
                      x1="840"
                      y1="0"
                      x2="840"
                      y2="436"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#F0DFC1" />
                      <stop offset="0.791452" stopColor="#C89452" />
                      <stop offset="1" stopColor="#B07F44" />
                    </linearGradient>
                  </defs>
                  <rect
                    x="0.5"
                    y="0.5"
                    width="calc(100% - 1px)"
                    height="calc(100% - 1px)"
                    rx="2.083vw"
                    ry="2.083vw"
                    fill="none"
                    stroke={`url(#${cardGradientId})`}
                    strokeWidth="1"
                  />
                </svg>

                <div
                  onClick={() => {
                    const itemActualIndex = itdx * 4 + idx;

                    setIndex(itemActualIndex);
                  }}
                  className="group relative flex flex-1 cursor-zoom-in overflow-hidden rounded-[max(2.083vw,30px)]"
                >
                  <Image
                    src={itm?.image_link?.trim() ?? ""}
                    alt={itm.name}
                    width={400}
                    height={600}
                    loading="lazy"
                    quality={85}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    className="h-full w-full transform cursor-pointer rounded-[max(2.083vw,30px)] object-cover transition-transform duration-300 group-hover:scale-110"
                  />

                  <div className="absolute inset-0 z-30 hidden items-center justify-center group-hover:flex">
                    <Search className="h-10 w-10 text-secondary" />
                  </div>

                  <div
                    className="pointer-events-none absolute inset-x-0 -bottom-4 z-30 h-4/5 transition-all duration-300 ease-linear group-hover:h-full"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 100%)",
                    }}
                  ></div>
                  <div className="absolute inset-0 z-50 flex flex-col justify-end gap-1 p-4">
                    <h5 className="text-xl text-secondary lg:text-3xl ltr:font-playfair">
                      {itm.name}
                    </h5>
                    <span className="text-sm lg:text-base ltr:font-opensans">
                      {itm?.title} - {itm.year}
                    </span>
                  </div>
                </div>
                {itm.description && (
                  <div className="flex-[0.3] flex-shrink-0 px-4 text-sm text-secondary/60">
                    <p>{itm?.description}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

function MobileCards({
  data,
  setIndex,
}: {
  data: HonoreesData[];
  setIndex: (index: number) => void;
}) {
  const cardGradientId = useId();

  return (
    <div className="relative flex h-full flex-wrap gap-6 overflow-hidden">
      {data.map((item, idx) => (
        <div
          key={item.id}
          className={
            "relative min-w-full flex-1 sm:min-w-[40%] sm:max-w-[49%] lg:min-w-[30%] lg:max-w-[33%]"
          }
        >
          <div
            className={cn(
              "flex flex-col gap-4 rounded-[max(2.083vw,30px)] bg-[#3A285C38] p-4 pb-8 backdrop-blur-md sm:h-[500px]",
              item.absolute ? "bottom-0 left-0 right-0 lg:absolute" : "",
              !item.description && "pb-4"
            )}
            style={{ height: `${item.height}px` }}
          >
            <svg
              className="pointer-events-none absolute inset-0 z-10 h-full w-full"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
            >
              <defs>
                <linearGradient
                  id={cardGradientId}
                  x1="840"
                  y1="0"
                  x2="840"
                  y2="436"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#F0DFC1" />
                  <stop offset="0.791452" stopColor="#C89452" />
                  <stop offset="1" stopColor="#B07F44" />
                </linearGradient>
              </defs>
              <rect
                x="0.5"
                y="0.5"
                width="calc(100% - 1px)"
                height="calc(100% - 1px)"
                rx="max(2.083vw,20px)"
                ry="max(2.083vw,20px)"
                fill="none"
                stroke={`url(#${cardGradientId})`}
                strokeWidth="1"
              />
            </svg>
            <div
              onClick={() => setIndex(idx)}
              className="group relative flex flex-1 cursor-zoom-in overflow-hidden rounded-[max(2.083vw,20px)]"
            >
              <Image
                src={item?.image_link.trim() ?? ""}
                alt={item.name}
                width={400}
                height={600}
                loading="lazy"
                quality={85}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className="h-full w-full transform cursor-pointer rounded-[max(2.083vw,20px)] object-cover transition-transform duration-300 group-hover:scale-110"
              />

              <div className="absolute inset-0 z-30 hidden items-center justify-center group-hover:flex">
                <Search className="h-10 w-10 text-secondary" />
              </div>

              <div
                className="pointer-events-none absolute inset-x-0 -bottom-4 z-30 h-4/5 transition-all duration-300 ease-linear group-hover:h-full"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 100%)",
                }}
              ></div>
              <div className="absolute inset-0 z-50 flex flex-col justify-end gap-1 p-4">
                <h5 className="text-xl text-secondary lg:text-3xl ltr:font-playfair">
                  {item.name}
                </h5>
                <span className="text-sm lg:text-base ltr:font-opensans">
                  {item?.title} - {item.year}
                </span>
              </div>
            </div>
            {item.description && (
              <div className="flex-[0.3] flex-shrink-0 px-4 text-sm text-secondary/60">
                <p>{item?.description}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
